import React, { useEffect, useState } from "react";
import { useChannelStateContext, useChatContext } from "stream-chat-react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { usePermission } from "../../hooks/usePermission";
import { ROLES } from "../../config/roles";
import { tenderService, playerService } from "../../services/api";
import api from "../../services/api";
import { isClubAgentPitch } from "../../utils/chatContext";
import { deriveOrigin } from "../../utils/laneMapping";
import ThreadContextCard from "./ThreadContextCard";
import { CONTEXT_ORIGINS } from "./threadContext";

// Intent pill palette (design frame 1f). Keyed on the canonical intent label
// strings; unknown labels fall back to the neutral "Manual" swatch.
const INTENT_PILL_COLORS = {
  "Player Opportunity": { bg: "#EAF2FF", fg: "#1466D8" },
  "Discuss about Player": { bg: "#F4F0FF", fg: "#6941C6" },
  Networking: { bg: "#E5F5F6", fg: "#0E7C86" },
  "Partnership Request": { bg: "#EEF0FF", fg: "#444CE7" },
  Partnership: { bg: "#EEF0FF", fg: "#444CE7" },
  Manual: { bg: "#F2F4F7", fg: "#475467" },
};
const NEUTRAL_INTENT_PILL = { bg: "#F2F4F7", fg: "#475467" };

const CustomWindow = ({ children }) => {
  const { channel } = useChannelStateContext();

  // Permission checks for pitch actions
  const canAcceptPitches = usePermission("accept_pitches");
  const { client } = useChatContext();
  const [showPitchAcceptModal, setShowPitchAcceptModal] = useState(false);
  const [showPitchRejectModal, setShowPitchRejectModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const navigate = useNavigate();

  const { user, roles, currentRole } = useSelector((state) => state.auth);
    const currentAccountId =
      user?.account_id != null
        ? String(user.account_id)
        : user?.id != null
        ? String(user.id)
        : null;
  // Genuine club↔agent pitch only — agent-tender response chats reuse the
  // `pitch-<id>` cid but must not mount the pitch tender card.
  const internalIsPitchChat = isClubAgentPitch(channel);

  // Agency of the other party in this thread. Empty for an agent without an
  // agency — DECISION-042: that absence renders nothing (see the accept modal).
  const counterpartAgencyName =
    channel?.data?.users?.find((u) => u.id !== client?.userID)?.agency_name ||
    "";

  // Origin/context pill row (design Turn 1) for origin-born threads. The request
  // /last-minute backend stamps these labels on the channel; render the quiet
  // context row only when they exist so all other thread types are unaffected.
  const intentLabel =
    channel?.data?.tp_intent_label ||
    channel?.data?.request_intent_label ||
    null;
  const originLabel = channel?.data?.request_origin_label || null;
  const intentColors = intentLabel
    ? INTENT_PILL_COLORS[intentLabel] || NEUTRAL_INTENT_PILL
    : null;

  // D8/D9: threads born from a canonical origin render the viewer-relative
  // ThreadContextCard below; the legacy label-card is suppressed for them so the
  // two never stack. Legacy channels (old request_*_label stamps, no canonical
  // origin) keep the old card as their fallback (Bible V5 "minimal state").
  const hasCanonicalContext = CONTEXT_ORIGINS.has(deriveOrigin(channel?.data || {}));

  // Extra context fields the request / last-minute backend stamps on the channel
  // (design Turn 1 / frame 1m). Every one is optional — a field that isn't
  // present is simply dropped, so legacy DMs with no labels keep today's minimal
  // (nothing-rendered) state rather than a faked card.
  const playerRef = channel?.data?.request_player_label || null;
  const listingId = channel?.data?.lm_listing_id ?? null;
  const openedAt = channel?.data?.created_at || null;
  const openedDate = (() => {
    if (!openedAt) return null;
    const d = new Date(openedAt);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  })();
  // Title follows the design's "<Intent> — <Player>" pattern; either half may be
  // absent. The card as a whole only renders when there's a label to anchor it.
  const contextTitle = [intentLabel, playerRef].filter(Boolean).join(" — ");
  const contextMeta = [originLabel, listingId != null ? `listing #${listingId}` : null]
    .filter(Boolean)
    .join(" · ");

    const channelAdminAccountId =
      channel?.data?.club_admin_account_id ??
      channel?.data?.pitch_details?.club_admin_account_id ??
      null;

    const normalizedChannelAdminId =
      channelAdminAccountId != null ? String(channelAdminAccountId) : null;

    const isChannelAdmin =
      Boolean(currentAccountId) &&
      Boolean(normalizedChannelAdminId) &&
      normalizedChannelAdminId === currentAccountId;

  // State for checking if this is a claim representation chat
  const [isClaimRepresentationChat, setIsClaimRepresentationChat] =
    useState(false);
  const [playerData, setPlayerData] = useState(null);
  const [resolvedPitchPlayer, setResolvedPitchPlayer] = useState(null);

  // Handle pitch acceptance
  const handleAcceptPitch = async () => {
    setShowPitchAcceptModal(false);
    const pitchId = channel?.data?.id;
    // const tenderId = channel?.data?.tender_id;

    if (!pitchId) {
      toast.error("Pitch ID not found");
      return;
    }

    const realPitchId = pitchId.split("-")[1];

    try {
      // Update pitch status to "deal" before navigating to contract creation
      const response = await tenderService.updatePitchStatus(
        realPitchId,
        "deal"
      );

      if (response.success) {
        console.log("Pitch status updated to deal:", response.data);
        // Navigate to contract creation page
        navigate(`/documents/create-contract?pitchId=${realPitchId}`);
      } else {
        toast.error(response.error || "Failed to update pitch status");
      }
    } catch (error) {
      console.error("Error updating pitch status:", error);
      toast.error("Failed to update pitch status. Please try again.");
    }
  };

  // Handle pitch rejection
  const handleRejectPitch = async () => {
    setShowPitchRejectModal(false);
    const pitchId = channel?.data?.id;

    if (!pitchId) {
      toast.error("Pitch ID not found");
      return;
    }

    const realPitchId = pitchId.split("-")[1];

    try {
      // Update pitch status to "rejected"
      const response = await tenderService.updatePitchStatus(
        realPitchId,
        "rejected"
      );

      if (response.success) {
        console.log("Pitch status updated to rejected:", response.data);
        toast.success("Pitch rejected successfully");
      } else {
        toast.error(response.error || "Failed to reject pitch");
      }
    } catch (error) {
      console.error("Error rejecting pitch:", error);
      toast.error("Failed to reject pitch. Please try again.");
    }
  };

  // Handle send proposal action in representation chat
  const handleSendProposalFromChat = async () => {
    try {
      // Get player_sportmonks_id and player_email from channel custom context
      console.log("channel", channel);
      const playerSportmonksId =
        channel?.data?._tp_custom_context?.player_sportmonks_id;
      const playerEmail = channel?.data?._tp_custom_context?.player_email;

      if (!playerSportmonksId) {
        toast.error("Player information not found");
        return;
      }

      // Fetch the player's full data using the sportmonks ID
      const playerResponse = await playerService.getPublicPlayerProfile(
        playerSportmonksId
      );

      if (!playerResponse.success) {
        toast.error("Failed to fetch player data");
        return;
      }

      // Navigate to invite player page with the player data and email
      navigate("/agent/invite-player", {
        state: {
          selectedPlayer: playerResponse.data,
          email: playerEmail || "",
          fromRepresentationChat: true,
        },
      });
    } catch (error) {
      console.error("Error handling send proposal:", error);
      toast.error("Failed to load player data. Please try again.");
    }
  };

  // Handle download chat history with backend integration
  const handleDownloadChatHistory = async () => {
    if (!channel || !client) return;
    setIsDownloading(true);

    try {
      // Get pitch ID from channel data
      const pitchId = channel?.data?.pitch_id;
      if (!pitchId) {
        toast.error("Pitch ID not found. Cannot proceed with export.");
        return;
      }

      // Step 1: Update pitch status to external_deal
      console.log(
        "Updating pitch status to external_deal for pitch ID:",
        pitchId
      );
      const statusUpdateResponse = await tenderService.updatePitchStatus(
        pitchId,
        "external_deal"
      );

      if (!statusUpdateResponse.success) {
        toast.error(
          statusUpdateResponse.error || "Failed to update pitch status"
        );
        return;
      }

      console.log(
        "Pitch status updated successfully:",
        statusUpdateResponse.data
      );

      // Step 2: Get export_task_id from status update response
      let taskId = statusUpdateResponse.data?.results?.export_task_id;

      // If no task_id from status update, manually trigger export
      if (!taskId) {
        console.log(
          "No export task ID from status update, manually triggering export..."
        );
        const exportResponse = await api.post(
          `/dashboard/club/tender-pitches/${pitchId}/export-chat/`
        );

        if (!exportResponse.data?.success) {
          toast.error("Failed to start chat export");
          return;
        }

        taskId = exportResponse.data.task_id;
      }

      if (!taskId) {
        toast.error("No export task ID received");
        return;
      }

      console.log("Export task ID:", taskId);

      // Step 3: Poll for export completion (every 10 seconds as per MD)
      let attempts = 0;
      const maxAttempts = 12; // ~2 minutes at 10s interval
      let isCompleted = false;

      while (attempts < maxAttempts && !isCompleted) {
        attempts += 1;
        console.log(
          `Polling export status, attempt ${attempts}/${maxAttempts}`
        );

        try {
          const statusResponse = await api.get(
            `/dashboard/club/tender-pitches/${pitchId}/export-chat-status/`,
            {
              params: { task_id: taskId },
            }
          );

          if (statusResponse.data?.success) {
            const { status } = statusResponse.data;

            if (status === "completed") {
              isCompleted = true;
              console.log("Export completed successfully");
              break;
            }

            if (status === "failed") {
              toast.error("Chat export failed. Please try again.");
              return;
            }

            // Still pending, continue polling
            console.log("Export still pending, status:", status);
          } else {
            console.log("Status response not successful:", statusResponse.data);
          }
        } catch (statusError) {
          console.error("Error polling export status:", statusError);
          // Continue polling even if one request fails
        }

        // Wait 10 seconds before next poll (as per MD)
        await new Promise((r) => setTimeout(r, 10000));
      }

      // Step 4: Download TXT file when completed
      if (isCompleted) {
        try {
          console.log("Downloading TXT file...");

          // Download TXT file using the export-chat-text endpoint
          const txtResponse = await api.get(
            `/dashboard/club/tender-pitches/${pitchId}/export-chat-text/`,
            {
              params: { task_id: taskId },
              responseType: "blob", // Important: get blob response for file download
            }
          );

          // Create download link for TXT file
          const blob = new Blob([txtResponse.data], { type: "text/plain" });
          const txtUrl = URL.createObjectURL(blob);
          const txtLink = document.createElement("a");
          txtLink.href = txtUrl;
          txtLink.download = `pitch-${pitchId}-chat.txt`;
          document.body.appendChild(txtLink);
          txtLink.click();
          document.body.removeChild(txtLink);
          URL.revokeObjectURL(txtUrl);

          setShowDownloadModal(false);
          toast.success(
            "Chat history exported (TXT) and emailed to both parties."
          );
        } catch (txtError) {
          console.error("Error downloading TXT file:", txtError);
          toast.error("Failed to download TXT file. Please try again.");
        }
      } else {
        toast.error("Export timed out. Please try again or contact support.");
      }
    } catch (error) {
      console.error("Error downloading chat history:", error);
      toast.error(
        error?.response?.data?.message ||
          "Failed to download chat history. Please try again."
      );
    } finally {
      setIsDownloading(false);
    }
  };

  // Check if the other user (player) has claim representation enabled
  useEffect(() => {
    const checkClaimRepresentation = async () => {
      if (
        !channel ||
        !client ||
        (currentRole !== ROLES.Agent && currentRole !== ROLES.AgencyAdmin)
      ) {
        setIsClaimRepresentationChat(false);
        setPlayerData(null);
        return;
      }

      const isClaimRepresentation =
        channel?.data?._tp_custom_context?.is_claim_representation;
      setIsClaimRepresentationChat(isClaimRepresentation);
    };

    checkClaimRepresentation();
  }, [channel, client, currentRole]);

  useEffect(() => {
    console.log("channel", channel);
    // Debug pitch status and document status
    if (channel?.data?.pitch_details) {
      console.log("Pitch details:", channel.data.pitch_details);
      console.log("Pitch status:", channel.data.pitch_details.pitch_status);
      console.log(
        "Document status:",
        channel.data.pitch_details.document_status
      );
    }
  }, [channel]);

  // Resolve player by Wyscout ID for pitch channels when details are incomplete
  useEffect(() => {
    const wsId = channel?.data?.pitch_details?.player_wyscout_id;
    const hasMinimalPlayer = Boolean(
      channel?.data?.pitch_details?.player_name &&
        channel?.data?.pitch_details?.player_image
    );

    if (
      !internalIsPitchChat ||
      !wsId ||
      hasMinimalPlayer ||
      resolvedPitchPlayer
    ) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const resp = await playerService.getPublicPlayerProfile(wsId);
        if (!cancelled && resp?.success) {
          setResolvedPitchPlayer(resp.data);
        }
      } catch (e) {
        // Silently ignore; UI falls back gracefully
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [internalIsPitchChat, channel, resolvedPitchPlayer]);

  return (
    <>
      {/* Render pitch header if needed */}
      {isChannelAdmin && internalIsPitchChat && (
        <div className="flex-none px-3 pt-2 pb-3 bg-[#F8F9FB]">
          {(() => {
            console.log(
              "Rendering pitch banner with status:",
              channel?.data?.pitch_details?.pitch_status
            );
            return null;
          })()}
          {/* The request bar was authored desktop-only: one non-wrapping row
              with a fixed gap-10 and an unshrinkable button group. At 360 the
              row was wider than the pane, so the Accept/Reject group ran past
              the right edge and "Reject" was cut mid-word. Per the mobile rule
              the row now STACKS instead of clipping — info block over action
              block below md, unchanged desktop layout from md up. The hairline
              dividers only make sense inside a single row, so they are hidden
              while the blocks are stacked. */}
          <div className="bg-white rounded-[20px] px-4 py-4 md:px-[27px] md:py-5 border border-[#E5E7EB] shadow-sm">
            <div className="flex flex-col items-stretch gap-4 md:flex-row md:items-center md:justify-between md:gap-10">
              <div className="flex flex-col items-start gap-3 min-w-0 md:flex-row md:items-center md:gap-5">
                <div className="flex flex-col items-start gap-3 min-w-0 md:flex-row md:items-center md:gap-5">
                  <div className="min-w-0">
                    <div className="text-xs text-[#646B7E] mb-2">
                      Current Request
                    </div>
                    <div className="font-medium text-base text-black">
                      {channel?.data?.tender_details?.position
                        ? channel.data.tender_details.position
                            .replace(/_/g, " ")
                            .split(" ")
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() + word.slice(1)
                            )
                            .join(" ")
                        : "Not specified"}
                    </div>
                  </div>
                  <div className="hidden md:block w-px h-full bg-[rgba(0,0,0,0.2)]"></div>
                  <div className="min-w-0">
                    <div className="text-xs text-[#646B7E] mb-2">Type</div>
                    <div className="font-medium text-base text-black">
                      Buy
                    </div>{" "}
                    {/* Static as per Figma */}
                  </div>
                </div>
                <div className="hidden md:block w-px h-full bg-[rgba(0,0,0,0.2)]"></div>
                <div className="min-w-0">
                  <div className="text-xs text-[#646B7E] mb-2">
                    Pitched Player by the agent
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Placeholder for player avatar - ideally from channel.data.pitch_details.player_image */}

                    {channel?.data?.pitch_details?.player_image ? (
                      <img
                        src={channel?.data?.pitch_details?.player_image}
                        alt="Pitched Player"
                        className="w-5 h-5 rounded-full object-contain"
                      />
                    ) : (
                      <div className="w-5 h-5 bg-gray-300 rounded-full flex items-center justify-center text-xs text-gray-600">
                        {(
                          channel?.data?.pitch_details?.player_name ||
                          resolvedPitchPlayer?.name ||
                          "P"
                        )
                          .substring(0, 1)
                          .toUpperCase()}
                      </div>
                    )}

                    <div className="font-medium text-base text-[#2283FF]">
                      {channel?.data?.pitch_details?.player_name ||
                        resolvedPitchPlayer?.name ||
                        "Player Name Unavailable"}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 md:flex-nowrap md:flex-shrink-0">
                {/* Show Accept/Reject buttons only when pitch_status is "accepted" and user has permission */}
                {channel?.data?.pitch_details?.pitch_status === "accepted" &&
                canAcceptPitches ? (
                  <>
                    <button
                      onClick={() => setShowPitchRejectModal(true)}
                      className="px-3 py-2.5 text-sm text-[#FF3B30] border border-[rgba(255,45,85,0.5)] rounded-[5px] hover:bg-red-50 transition-colors duration-150 shadow-sm"
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => setShowPitchAcceptModal(true)}
                      className="px-3 py-2.5 text-sm text-[#F5F5F5] bg-[#2283FF] rounded-[5px] hover:bg-blue-700 transition-colors duration-150 shadow-sm"
                    >
                      Accept deal
                    </button>
                  </>
                ) : channel?.data?.pitch_details?.pitch_status === "deal" ? (
                  /* Show different content based on document_status when pitch_status is "deal" */
                  (() => {
                    const documentStatus =
                      channel?.data?.pitch_details?.document_status;

                    if (documentStatus === "pending_signatures") {
                      return (
                        <div className="px-3 py-2.5 text-sm text-[#B96C00] bg-[#FF95001A] rounded-[5px]">
                          Pending signatures
                        </div>
                      );
                    } else if (documentStatus === "partially_signed") {
                      return (
                        <div className="px-3 py-2.5 text-sm text-[#B96C00] bg-[#FF95001A]  rounded-[5px]">
                          Partially signed
                        </div>
                      );
                    } else if (documentStatus === "fully_signed") {
                      return (
                        <div
                          onClick={() => {
                            navigate("/documents?tab=signed");
                          }}
                          className="px-3 py-2.5 text-sm text-[#347255] bg-[#42936D1A] rounded-[5px] cursor-pointer"
                        >
                          Contract signed by both parties. View contract
                        </div>
                      );
                    } else {
                      /* Show Create Contract button when pitch_status is "deal" but no document_status */
                      return (
                        <button
                          onClick={() => {
                            const pitchId = channel?.data?.id;
                            const realPitchId = pitchId.split("-")[1];
                            if (pitchId) {
                              navigate(
                                `/documents/create-contract?pitchId=${realPitchId}`
                              );
                            } else {
                              toast.error("Pitch information not found");
                            }
                          }}
                          className="px-3 py-2.5 text-sm text-[#F5F5F5] bg-[#2283FF] rounded-[5px] hover:bg-blue-700 transition-colors duration-150 shadow-sm"
                        >
                          Create Contract
                        </button>
                      );
                    }
                  })()
                ) : channel?.data?.pitch_details?.pitch_status === "signed" ? (
                  <div className="px-3 py-2.5 text-sm text-[#347255] bg-[#42936D1A] rounded-[5px] ">
                    Contract signed by both parties. View contract
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Render representation banner for agent-player chats (Agents only) */}
      {isClaimRepresentationChat &&
        (currentRole === ROLES.Agent || currentRole === ROLES.AgencyAdmin) && (
          <div className="flex-none px-3 pt-2 pb-3 bg-[#F8F9FB]">
            {/* Identical shape to the request bar above, and identically
                desktop-only: at 360 the "Send Proposal" button ran off the
                right edge. Same stacking treatment. */}
            <div className="bg-white rounded-[20px] px-4 py-4 md:px-[27px] md:py-5 border border-[#E5E7EB] shadow-sm">
              <div className="flex flex-col items-stretch gap-4 md:flex-row md:items-center md:justify-between md:gap-10">
                <div className="flex flex-col items-start gap-3 min-w-0 md:flex-row md:items-center md:gap-5">
                  <div className="flex flex-col items-start gap-3 min-w-0 md:flex-row md:items-center md:gap-5">
                    <div className="min-w-0">
                      <div className="text-xs text-[#646B7E] mb-2">
                        Representation Chat
                      </div>
                      <div className="font-medium text-base text-black">
                        Claim Representation Discussion
                      </div>
                    </div>
                    <div className="hidden md:block w-px h-full bg-[rgba(0,0,0,0.2)]"></div>
                    <div className="min-w-0">
                      <div className="text-xs text-[#646B7E] mb-2">Player</div>
                      <div className="font-medium text-base text-[#2283FF]">
                        {playerData?.name ||
                          channel?.data?.custom?.users?.find(
                            (u) => u.id !== client.userID
                          )?.name ||
                          channel?.data?.custom?.users?.find(
                            (u) => u.id !== client.userID
                          )?.email ||
                          "Player"}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 md:flex-nowrap md:flex-shrink-0">
                  <button
                    onClick={handleSendProposalFromChat}
                    className="px-3 py-2.5 text-sm text-[#F5F5F5] bg-[#2283FF] rounded-[5px] hover:bg-blue-700 transition-colors duration-150 shadow-sm"
                  >
                    Send Proposal
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Accept Modal */}
      {showPitchAcceptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-10 max-w-2xl w-full mx-auto shadow-xl flex flex-col justify-center relative">
            {/* Close icon (replaces Cancel button) */}
            <button
              type="button"
              onClick={() => setShowPitchAcceptModal(false)}
              aria-label="Close"
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-[#1A212F]"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <h3 className="text-[20px] font-bold text-[#1A212F] mb-3 text-center">
              Are you sure you want to accept deal for '
              {channel?.data?.pitch_details?.player_name || "Player Name"}' -{" "}
              {channel?.data?.tender_details?.position
                ? channel.data.tender_details.position
                    .replace(/_/g, " ")
                    .split(" ")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")
                : "Position"}
              ?
            </h3>
            <p className="text-[14px] text-[#4B4B4B] mb-6 leading-relaxed text-center">
              You're about to begin the contract process for{" "}
              <span className="font-bold text-[#1A212F]">
                {channel?.data?.pitch_details?.player_name || "Player Name"}
              </span>
              {/* DECISION-042 (Marin, 2026-07-29): "kein Label. Auslassung
                  bleibt." An agent without an agency used to make this sentence
                  print the raw placeholder "Agency Name". The clause is dropped
                  instead — the sentence closes straight after the player, with
                  no dangling "as pitched by" and no stray space. */}
              {counterpartAgencyName && (
                <>
                  {" "}
                  as pitched by{" "}
                  <span className="font-bold text-[#1A212F]">
                    {counterpartAgencyName}
                  </span>
                </>
              )}
              . A standard or custom contract must be created, signed by both
              parties, and verified before the transfer is finalized.
            </p>

            <div
              className="px-4 py-2 rounded-lg mb-6"
              style={{
                background:
                  "linear-gradient(90deg, rgba(255, 77, 79, 0.2) 0%, rgba(250, 170, 0, 0.2) 100%)",
              }}
            >
              <p className="text-[12px] text-[#000000]">
                Note: If you wish to continue later, you can always access and
                complete the contract creation from the Documents Manager under
                your profile.
              </p>
            </div>

            <div className="flex space-x-3 w-full">
              {/* Replaces the old Cancel button — same handler that the
                  previous bottom link used (open the "Sign contract outside" flow). */}
              <button
                onClick={() => {
                  setShowPitchAcceptModal(false);
                  setShowDownloadModal(true);
                }}
                className="w-1/2 px-4 py-2 text-[14px] text-[#1E1E1E] rounded-lg border border-[#E6E6E6] bg-white"
                style={{ boxShadow: "0px 1px 5px 0px #00000014" }}
              >
                Sign contract outside
              </button>
              <button
                onClick={handleAcceptPitch}
                className="w-1/2 px-4 py-2 text-[14px] font-medium text-[#F5F5F5] bg-[#2283FF] rounded-lg"
                style={{ boxShadow: "0px 1px 5px 0px #00000014" }}
              >
                Accept & start Contract creation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showPitchRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-auto shadow-xl">
            <h3 className="text-lg font-semibold mb-3">
              Reject Deal Confirmation
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to reject this deal? This action cannot be
              undone, and the other party will be notified.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowPitchRejectModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectPitch}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Reject Deal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Download Chat History Modal */}
      {showDownloadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-10 max-w-2xl w-full mx-auto shadow-xl flex flex-col justify-center">
            <h3 className="text-[20px] font-bold text-[#1A212F] mb-3 text-center">
              Sign contract outside Transferpitch platform
            </h3>
            <p className="text-[14px] text-[#4B4B4B] mb-6 leading-relaxed text-center">
              By accepting this process, this chat will be permanently closed.
            </p>

            <div className="flex space-x-3 w-full">
              <button
                onClick={() => setShowDownloadModal(false)}
                className="w-1/2 px-4 py-2 text-[14px] text-[#1E1E1E] rounded-lg border border-[#E6E6E6] bg-white"
                style={{ boxShadow: "0px 1px 5px 0px #00000014" }}
              >
                Cancel
              </button>
              <button
                onClick={handleDownloadChatHistory}
                disabled={isDownloading}
                className={`w-1/2 px-4 py-2 text-[14px] font-medium rounded-lg ${
                  isDownloading
                    ? "bg-[#7AAEF9] cursor-not-allowed text-white"
                    : "bg-[#2283FF] text-[#F5F5F5]"
                }`}
                style={{ boxShadow: "0px 1px 5px 0px #00000014" }}
              >
                {isDownloading ? "Preparing…" : "Download chat history"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Context card (design Turn 1 / frame 1m) — the thread's origin story: an
          icon tile, the intent + origin pills, a "<Intent> — <Player>" title, a
          meta line (origin · listing #id) and, when the thread came from a Last
          Minute listing, a "View listing" deep-link. Renders above the message
          list only for origin-born threads carrying the labels; legacy DMs keep
          their bare state. Every field self-gates — nothing is invented.

          SUPERSEDED for canonical-origin threads by ThreadContextCard (D8/D9),
          mounted below; this legacy card only shows for pre-V5 label-only
          channels so the two never stack. */}
      <ThreadContextCard />
      {!hasCanonicalContext && (intentLabel || originLabel) && (
        <div className="flex-none px-3 pt-2 pb-1 bg-[#F8F9FB]">
          <div
            className="flex items-start gap-3 rounded-[12px] border border-[#E9EDF2] bg-white"
            style={{ padding: "10px 12px" }}
          >
            {/* Icon tile */}
            <span
              aria-hidden="true"
              className="flex flex-shrink-0 items-center justify-center rounded-[9px]"
              style={{
                width: 34,
                height: 34,
                background: intentColors ? intentColors.bg : "#F2F4F7",
                color: intentColors ? intentColors.fg : "#475467",
              }}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.7}
                  d="M12 3l2.09 4.26L18.9 8l-3.45 3.36.82 4.78L12 13.9l-4.27 2.24.82-4.78L5.1 8l4.81-.74L12 3z"
                />
              </svg>
            </span>

            <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 3 }}>
              {/* Pills */}
              <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
                {intentLabel && (
                  <span
                    style={{
                      padding: "2px 9px",
                      borderRadius: 999,
                      background: intentColors.bg,
                      color: intentColors.fg,
                      fontSize: 10.5,
                      fontWeight: 600,
                    }}
                  >
                    {intentLabel}
                  </span>
                )}
                {originLabel && (
                  <span
                    className="rounded-[6px] font-medium"
                    style={{
                      padding: "2px 7px",
                      background: "#FFFFFF",
                      border: "1px solid #E6EAF0",
                      color: "#667085",
                      fontSize: 10.5,
                    }}
                  >
                    {originLabel}
                  </span>
                )}
              </div>

              {/* Title */}
              {contextTitle && (
                <span className="truncate text-[13px] font-bold text-[#101828]" title={contextTitle}>
                  {contextTitle}
                </span>
              )}

              {/* Meta line */}
              {contextMeta && (
                <span className="truncate text-[11.5px] text-[#667085]">{contextMeta}</span>
              )}

              {/* Opening date (frame 1m). Only when the channel carries one. */}
              {openedDate && (
                <span className="text-[11px] text-[#98A2B3]">Thread opened {openedDate}</span>
              )}

              {/* View listing — only when a real Last Minute listing id exists,
                  linking to the existing /last-minute?listing=<id> route. */}
              {listingId != null && (
                <button
                  type="button"
                  onClick={() => navigate(`/last-minute?listing=${listingId}`)}
                  className="mt-0.5 inline-flex w-fit items-center gap-1 text-[11.5px] font-semibold text-[#1466D8]"
                >
                  View listing
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                    <path
                      d="M2 6h8M7 3l3 3-3 3"
                      stroke="#1466D8"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Let the normal Window from Stream Chat render */}
      {children}
    </>
  );
};

export default CustomWindow;
